Returns the public details of one specific campaign for the authenticated tenant.

## What this endpoint returns

- public campaign identity;
- campaign status and type;
- main operational configuration such as concurrency and retries;
- progress counters and high-level totals;
- public references to provider, agent, and SIP trunk when present.

<ApiCodeExamples exampleId="campaignDetail" />

<ApiResponseExamples exampleId="campaignDetail" />

## Main fields in the `200` response

- `id`: campaign ID.
- `canonical_url`: canonical Contact-Tel app link for the campaign.
- `created_by_id`, `created_by_name`: creator user reference.
- `ai_agent_provider_id`, `ai_agent_id`, `sip_trunk_id`: public references tied to the configuration.
- `ai_agent_provider_name`, `ai_agent_name`, `sip_trunk_name`: display labels for related resources.
- `status_reason`: sanitized public reason code for special states.
- `schedule`: operational schedule configured for the campaign.
- `transfer_target_type`, `transfer_target_id`, `transfer_target_name`, `transfer_target_extension`: public transfer target when applicable.
- `ivr_dtmf_map`: DTMF map configured for Reverse IVR campaigns.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: net, gross and refunded AI consumption.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: net, gross and refunded telephony consumption.
- `conversion_rate`: public conversion rate over eligible calls.

## Option reference

- `status`: `draft` is not ready to run; `processing` is preparing/importing contacts; `ready` can start; `active` is running; `paused` is stopped temporarily; `completed` finished; `cancelled` was cancelled.
- `status_reason`: `import_failed` means import blocked the campaign; `config_error` means configuration blocked execution; `paused` is a normal operational pause; `null` means no extra public reason.
- `campaign_type`: `ai` is an AI-agent campaign; `reverse_ivr` is a Reverse IVR campaign.
- `ai_agent_provider_type`: `cloud` is a cloud provider; `asterisk` is local Asterisk/SIP; `sip` is an external SIP provider.
- `transfer_target_type`: `user` transfers to a specific user/operator; `group` transfers to an attendance group; `null` means no transfer target configured.
- `import_status`: `idle`, `processing`, `completed`, or `failed`.

## When to use this endpoint

- fetch one campaign after getting its `id` from the list endpoint;
- sync public campaign state/configuration into external systems;
- validate campaign operational details without opening the product UI.
